import * as z from 'zod'

export const moneySchema = z.object({
  amount: z.number().optional(),
  amountMinor: z.number(),
  currency: z.string(),
})

export const bookingExtraSchema = z.object({
  name: z.string(),
  price: moneySchema.nullable(),
  quantity: z.number(),
})

export const campsiteLocationSchema = z.object({
  directions: z.string(),
})

export const campsiteSchema = z.object({
  accessCode: z.string().nullable().optional(),
  arrivalTime: z.string().nullable().optional(),
  campsiteNotices: z
    .object({ isCovid: z.boolean(), text: z.string() })
    .array()
    .optional(),
  cardTypes: z
    .object({
      name: z.string(),
      slug: z.string(),
    })
    .array()
    .optional(),
  cancellationPolicyNeedsTranslation: z.boolean(),
  currency: z.string(),
  email: z.string(),
  fullAddress2: z.string(),
  location: campsiteLocationSchema,
  name: z.string(),
  notices: z.string().optional(),
  noticesNeedsTranslation: z.boolean().optional(),
  path: z.string(),
  point: z.string().nullable(),
  rating10: z.number(),
  rateCount: z.number(),
  secondaryPhone: z.string().nullable(),
  slug: z.string(),
  tel: z.string(),
  termsAndConditions: z.string(),
  termsAndConditionsNeedsTranslation: z.boolean(),
  timezone: z.string(),
  usePermittedArrivalDepartTimes: z.boolean(),
  warnings: z.string().array().optional(),
})

export const primaryPhotoSchema = z.object({
  caption: z.string(),
  url: z.object({
    masterImage: z.string(),
  }),
})

const pitchTypeCategory = z.object({
  name: z.string(),
  slug: z.string(),
})

export const pitchTypeSchema = z.object({
  allowedSubcategories: z.string().array(),
  bedroomsText: z.string(),
  bringBedLinenHtml: z.string().nullable().optional(),
  campsite: campsiteSchema,
  categories: pitchTypeCategory.array(),
  dimensions: z.string().nullable(),
  dimensionsDetailed: z
    .object({
      areaFt: z.number().nullable(),
      areaM: z.number().nullable(),
      customDimensions: z.string().nullable(),
      depthFt: z.number().nullable(),
      depthM: z.number().nullable(),
      dimensionsType: z.string().nullable(),
      dimensionsTypeCode: z.string().nullable(),
      widthFt: z.number().nullable(),
      widthM: z.number().nullable(),
    })
    .optional()
    .nullable(),
  description: z.string(),
  groundType: z.string().nullable(),
  isByo: z.boolean(),
  name: z.string(),
  oneUnitPerPitch: z.boolean(),
  primaryPhoto: primaryPhotoSchema.nullable(),
  requirePartyNames: z.boolean(),
  twinAxleAccepted: z.boolean().nullable(),
  requireCarRegistration: z.boolean(),
  restrictions: z
    .object({
      name: z.string(),
      amenityIcon: z.string().nullable(),
    })
    .array()
    .optional(),
})

export const cardDetailsSchema = z.object({
  expMonth: z.number().nullable(),
  expYear: z.number().nullable(),
  last4: z.string().nullable(),
  type: z.string().nullable(),
})

export const bookingTaxSchema = z.object({
  amount: moneySchema,
  hidden: z.boolean(),
  inclusive: z.boolean(),
  rate: z.union([z.string(), z.number()]).transform((v) => Number(v)),
  taxType: z.string(),
})

const stripeCampsitePayment = z.object({
  dueDate: z.string(),
  paymentLinkPaidDetails: z
    .object({
      amount: moneySchema,
      datetime: z.string(),
    })
    .nullable()
    .optional(),
  status: z.string(),
})

const arrivalTaxSchema = z.object({
  amount: moneySchema,
  inclusive: z.boolean(),
  taxType: z.string(),
})

const changeSchema = z.object({
  changedDate: z.string(),
  originalDate: z.string(),
})

const changesSchema = z
  .object({
    arrive: changeSchema.optional(),
    depart: changeSchema.optional(),
  })
  .nullable()

export const bookingThanksSchema = z.object({
  accommodationCost: moneySchema.optional(),
  adults: z.number(),
  address: z.string(),
  arrivalFrom: z.string().optional().nullable(),
  arrivalTo: z.string().optional().nullable(),
  arrive: z.string(),
  balanceDueDate: z.string().nullable(),
  bookAgainUrl: z.string(),
  bookingExtras: bookingExtraSchema.array().nullable(),
  bookingarrivaltaxSet: arrivalTaxSchema.array(),
  bookingtaxSet: bookingTaxSchema.array(),
  campsite_BalancePaymentTermsHtml: z.string().optional(),
  campsite_CampsiteTermsAndConditionsHtml: z.string(),
  campsite_PaymentTermsHtml: z.string(),
  campsite_TaxTermsHtml: z.string(),
  campsiteCancellationPolicyNeedsTranslation: z.boolean(),
  campsiteTermsAndConditionsNeedsTranslation: z.boolean(),
  campsiteUsePermittedArrivalDepartTimes: z.boolean(),
  cardDetails: cardDetailsSchema,
  carRegistrationNumber: z.string().nullable(),
  changes: changesSchema.nullable(),
  childAges: z.number().array(),
  city: z.string(),
  county: z.string(),
  commission: z.number(),
  currencyPreference: z.string().nullable(),
  country: z.string(),
  created: z.string(),
  depart: z.string(),
  departFrom: z.string().optional().nullable(),
  deposit: moneySchema,
  dimensionsDepth: z.string().optional().nullable(),
  dimensionsWidth: z.string().optional().nullable(),
  dogs: z.number().nullable().optional(),
  doorCode: z.string().nullable().optional(),
  email: z.string(),
  estimatedTimeOfArrival: z.string().nullable(),
  firstName: z.string(),
  groupName: z.string().nullable().optional(),
  lastName: z.string(),
  namesOfAllPartyMembers: z.string().nullable(),
  newsletterSubscribed: z.boolean().optional(),
  party: z.string().nullable(),
  pitchType: pitchTypeSchema,
  pitchtypeNameWithUnitNumber: z.string().optional(),
  postcode: z.string(),
  prettyId: z.string(),
  price: moneySchema,
  remainder: moneySchema,
  signature: z.string(),
  specialRequests: z.string(),
  stripecampsitepayment: stripeCampsitePayment.nullable(),
  stripePaymentLink: z.string().nullable(),
  telephone: z.string().nullable(),
  textMessage: z.boolean(),
  unitType: z
    .object({
      name: z.string(),
    })
    .nullable()
    .optional(),
  version: z.string(),
  typeAndApproxSizeOfUnit: z.string().nullable(),
})

export type BookingThanks = z.infer<typeof bookingThanksSchema>
export type BookingExtra = z.infer<typeof bookingExtraSchema>
